import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	AlertTriangle,
	ArrowLeft,
	ArrowRight,
	Building2,
	Calendar,
	Check,
	CheckCircle2,
	Circle,
	ClipboardCheck,
	Clock,
	DollarSign,
	Download,
	FileText,
	Loader2,
	Save,
	Send,
	Trash2,
	Upload,
	X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext } from "@/contexts/vendor-buyer-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { SubmitConfirmModal } from "@/components/vendor/SubmitConfirmModal";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { DocumentUploadModal } from "@/components/vendor/DocumentUploadModal";
import { PdfFormFillerV2 } from "@/components/pdf-filler";
import type { FormFillerData } from "@/components/pdf-filler/types";
import { useVendorProfile } from "@/contexts/vendor-buyer-context";

export const Route = createFileRoute("/vendor/$buyerSlug/bids/$bidId/edit")({
	component: VendorBidEditPage,
});

// Local form type with completion status
interface BidForm {
	id: string;
	name: string;
	description: string;
	required: boolean;
	status: "submitted" | "draft" | null;
	completed: boolean;
	formTemplateId: string | null;
	existingResponses: Record<string, unknown>;
}

// Local document type with upload status
interface BidDocument {
	id: string;
	name: string;
	description: string;
	required: boolean;
	uploaded: boolean;
	filePath?: string;
	fileSize?: number;
	mimeType?: string;
	originalFilename?: string;
}

interface BidCompliance {
	id: string;
	name: string;
	description: string;
	required: boolean;
	acknowledged: boolean;
}

// Compliance check with status for tracking acknowledgments
interface ComplianceWithStatus extends BidCompliance {
	solicitationComplianceCheckId: string;
}

const steps = [
	{ id: "overview", name: "Overview", icon: FileText },
	{ id: "forms", name: "Forms", icon: FileText },
	{ id: "documents", name: "Documents", icon: Upload },
	{ id: "compliance", name: "Compliance", icon: ClipboardCheck },
	{ id: "review", name: "Review & Submit", icon: CheckCircle2 },
];

function VendorBidEditPage() {
	const buyer = useBuyerContext();
	const vendorProfile = useVendorProfile();
	const { buyerSlug, bidId } = Route.useParams();
	const navigate = useNavigate();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const vendorEmail = user?.email ?? "";
	// Use the real email from Bidscope for signatures, fall back to portal email
	const signerEmail = vendorProfile.email || vendorEmail;

	// Query the bid
	const { data: bidData, isLoading: bidLoading } = useQuery(
		trpc.vendorPortal.bids.get.queryOptions({
			bidId,
		}),
	);

	const solicitationId = bidData?.solicitation_id;

	// Query solicitation data (only if we have the solicitation ID)
	const { data: solicitationData, isLoading: solicitationLoading } = useQuery({
		...trpc.public.solicitations.get.queryOptions({
			solicitationId: solicitationId ?? "",
		}),
		enabled: !!solicitationId,
	});

	// Query forms for this solicitation
	const { data: formsData, isLoading: formsLoading } = useQuery({
		...trpc.public.solicitationDetails.forms.queryOptions({
			solicitationId: solicitationId ?? "",
		}),
		enabled: !!solicitationId,
	});

	// Query required documents for this solicitation
	const { data: documentsData, isLoading: documentsLoading } = useQuery({
		...trpc.public.solicitationDetails.documents.queryOptions({
			solicitationId: solicitationId ?? "",
		}),
		enabled: !!solicitationId,
	});

	// Query existing form submissions for this vendor
	const { data: formSubmissionsData } = useQuery({
		...trpc.vendorPortal.formResponses.listByVendor.queryOptions({
			vendorEmail,
		}),
		enabled: !!vendorEmail,
	});


	// Query existing document uploads for this vendor
	const { data: documentSubmissionsData } = useQuery({
		...trpc.vendorPortal.documentUploads.listByVendor.queryOptions({
			vendorEmail,
		}),
		enabled: !!vendorEmail,
	});

	// Query compliance checks for this solicitation
	const { data: complianceData, isLoading: complianceLoading } = useQuery({
		...trpc.public.solicitationDetails.complianceChecks.queryOptions({
			solicitationId: solicitationId ?? "",
		}),
		enabled: !!solicitationId,
	});

	// Query existing compliance acknowledgments for this bid
	const { data: complianceAcksData } = useQuery({
		...trpc.vendorPortal.bids.getComplianceAcknowledgments.queryOptions({
			bidId,
		}),
		enabled: !!bidId,
	});

	// Get the organization ID for onboarding check (most specific: subtier > member > buyer)
	const targetOrgId =
		(solicitationData as { subtier_org_id?: string | null } | undefined)?.subtier_org_id ??
		(solicitationData as { member_org_id?: string | null } | undefined)?.member_org_id ??
		(solicitationData as { buyer_org_id?: string } | undefined)?.buyer_org_id;

	// Query vendor's onboarding progress for this organization
	const { data: onboardingProgress } = useQuery({
		...trpc.vendorPortal.onboarding.getVendorProgress.queryOptions({
			organizationId: targetOrgId ?? "",
		}),
		enabled: !!targetOrgId,
	});

	// Check if onboarding is complete
	const onboardingComplete = !targetOrgId || (onboardingProgress?.overall.percentage === 100);

	// Map to local types
	const solicitation = solicitationData
		? {
				id: solicitationData.id,
				title: solicitationData.title,
				description: solicitationData.description ?? "",
				type: solicitationData.type,
				status: solicitationData.status,
				dueDate: solicitationData.due_date,
				buyerOrgName: (solicitationData as { buyer_org?: { name: string } | null }).buyer_org?.name,
				memberOrgName: (solicitationData as { member_org?: { name: string } | null }).member_org?.name,
				contractPeriods: undefined as
					| { id: string; name: string; startDate: string; endDate: string }[]
					| undefined,
			}
		: null;

	// Map form submissions by solicitation_form_id for lookup - only "submitted" status counts
	// Must be memoized to prevent infinite render loops
	const submittedFormIds = useMemo(
		() => new Set(
			(formSubmissionsData ?? [])
				.filter((fs) => fs.solicitation_form_id && fs.status === "submitted")
				.map((fs) => fs.solicitation_form_id),
		),
		[formSubmissionsData],
	);

	// Track draft form IDs
	const draftFormIds = useMemo(
		() => new Set(
			(formSubmissionsData ?? [])
				.filter((fs) => fs.solicitation_form_id && fs.status === "draft")
				.map((fs) => fs.solicitation_form_id),
		),
		[formSubmissionsData],
	);

	// Track optimistic form status updates (must be declared before initialForms useMemo)
	const [optimisticFormStatus, setOptimisticFormStatus] = useState<
		Record<string, "submitted" | "draft" | "deleted">
	>({});

	// Track optimistic document uploads (must be declared before initialDocuments useMemo)
	const [optimisticDocUploads, setOptimisticDocUploads] = useState<
		Record<string, { uploaded: true; filePath: string; fileSize?: number; mimeType?: string; originalFilename?: string }>
	>({});

	// Initialize forms state from query data with completion status
	// Note: formsData returns { forms, archivedForms }, so access .forms
	const initialForms = useMemo<BidForm[]>(() => {
		const result = (formsData?.forms ?? []).map((f) => {
			// Find existing submission for this form
			const existingSubmission = (formSubmissionsData ?? []).find(
				(fs) => fs.solicitation_form_id === f.id
			);

			// Check for optimistic status first
			const optimisticStatus = optimisticFormStatus[f.id];
			// Determine form status: "submitted", "draft", or null (not started)
			let status: "submitted" | "draft" | null = null;
			if (optimisticStatus === "deleted") {
				status = null; // Form was deleted optimistically
			} else if (optimisticStatus === "submitted") {
				status = "submitted";
			} else if (optimisticStatus === "draft") {
				status = "draft";
			} else if (submittedFormIds.has(f.id)) {
				status = "submitted";
			} else if (draftFormIds.has(f.id)) {
				status = "draft";
			}

			const existingResponses = optimisticStatus === "deleted"
				? {}
				: (existingSubmission?.field_responses as Record<string, unknown>) ?? {};

			return {
				id: f.id,
				name: f.title,
				description: f.description ?? "",
				required: f.is_required,
				status,
				completed: status === "submitted",
				formTemplateId: f.form_template_id ?? null,
				existingResponses,
			};
		});

		return result;
	}, [formsData, submittedFormIds, draftFormIds, formSubmissionsData, optimisticFormStatus]);

	// Create a map of document submissions by requested_document_id for quick lookup
	const documentSubmissionsMap = useMemo(() => {
		const map = new Map<string, typeof documentSubmissionsData extends (infer T)[] ? T : never>();
		for (const ds of documentSubmissionsData ?? []) {
			// Keep the most recent submission per document
			if (!map.has(ds.requested_document_id) ||
				new Date(ds.uploaded_at) > new Date(map.get(ds.requested_document_id)!.uploaded_at)) {
				map.set(ds.requested_document_id, ds);
			}
		}
		return map;
	}, [documentSubmissionsData]);

	// Initialize documents state from query data with upload status
	const initialDocuments = useMemo<BidDocument[]>(
		() =>
			(documentsData ?? []).map((d) => {
				// Check for optimistic upload first
				const optimisticUpload = optimisticDocUploads[d.id];
				if (optimisticUpload) {
					return {
						id: d.id,
						name: d.title,
						description: d.description ?? "",
						required: d.is_required,
						uploaded: true,
						filePath: optimisticUpload.filePath,
						fileSize: optimisticUpload.fileSize,
						mimeType: optimisticUpload.mimeType,
						originalFilename: optimisticUpload.originalFilename,
					};
				}
				// Check for existing server submission
				const existingSubmission = documentSubmissionsMap.get(d.id);
				if (existingSubmission) {
					return {
						id: d.id,
						name: d.title,
						description: d.description ?? "",
						required: d.is_required,
						uploaded: true,
						filePath: existingSubmission.file_path,
						fileSize: existingSubmission.file_size ?? undefined,
						mimeType: existingSubmission.mime_type ?? undefined,
						originalFilename: existingSubmission.original_filename ?? undefined,
					};
				}
				return {
					id: d.id,
					name: d.title,
					description: d.description ?? "",
					required: d.is_required,
					uploaded: false,
				};
			}),
		[documentsData, documentSubmissionsMap, optimisticDocUploads],
	);

	// Map existing compliance acknowledgments by solicitation_compliance_check_id
	const acknowledgedComplianceIds = useMemo(
		() =>
			new Set(
				(complianceAcksData ?? [])
					.filter((ca) => ca.acknowledged)
					.map((ca) => ca.solicitation_compliance_check_id),
			),
		[complianceAcksData],
	);

	// Initialize compliance state from query data with acknowledgment status
	const initialCompliance = useMemo<ComplianceWithStatus[]>(
		() =>
			(complianceData ?? []).map((c) => ({
				id: c.id,
				solicitationComplianceCheckId: c.id,
				name: c.title,
				description: c.description ?? "",
				required: c.is_required,
				acknowledged: acknowledgedComplianceIds.has(c.id),
			})),
		[complianceData, acknowledgedComplianceIds],
	);

	const [currentStep, setCurrentStep] = useState(0);
	const [forms, setForms] = useState<BidForm[]>([]);
	const [documents, setDocuments] = useState<BidDocument[]>([]);
	const [compliance, setCompliance] = useState<ComplianceWithStatus[]>([]);
	const [formResponses, setFormResponses] = useState<
		Record<string, { fieldResponses: Record<string, unknown>; isSigned: boolean }>
	>({});

	// Sync forms and documents when data loads
	useEffect(() => {
		if (initialForms.length > 0) {
			setForms(initialForms);
		}
	}, [initialForms]);

	useEffect(() => {
		if (initialDocuments.length > 0) {
			setDocuments(initialDocuments);
		}
	}, [initialDocuments]);

	useEffect(() => {
		if (initialCompliance.length > 0) {
			setCompliance(initialCompliance);
		}
	}, [initialCompliance]);

	// Modal states
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState<BidDocument | null>(null);
	const [formToFill, setFormToFill] = useState<BidForm | null>(null);
	const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);

	// Period pricing state
	const [periodPricing, setPeriodPricing] = useState<Record<string, number>>({});

	const handlePriceChange = (periodId: string, value: string) => {
		const numValue = parseFloat(value.replace(/[^0-9.]/g, "")) || 0;
		setPeriodPricing({ ...periodPricing, [periodId]: numValue });
	};

	const getTotalPrice = () => {
		return Object.values(periodPricing).reduce((sum, price) => sum + price, 0);
	};

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
		}).format(amount);
	};

	// Save draft mutation
	const saveDraftMutation = useMutation(
		trpc.vendorPortal.bids.saveDraft.mutationOptions({
			onSuccess: () => {
				toast.success("Draft saved successfully");
				queryClient.invalidateQueries({ queryKey: ["vendorPortal", "bids"] });
			},
			onError: (error) => {
				toast.error(`Failed to save draft: ${error.message}`);
			},
		}),
	);

	// Submit bid mutation
	const submitBidMutation = useMutation(
		trpc.vendorPortal.bids.submit.mutationOptions({
			onSuccess: () => {
				toast.success("Bid submitted successfully!");
				queryClient.invalidateQueries({ queryKey: ["vendorPortal", "bids"] });
				navigate({ to: "/vendor/$buyerSlug/bids", params: { buyerSlug } });
			},
			onError: (error) => {
				toast.error(`Failed to submit bid: ${error.message}`);
			},
		}),
	);

	// Submit individual form response mutation (persists immediately)
	const submitFormResponseMutation = useMutation(
		trpc.vendorPortal.formResponses.submit.mutationOptions({
			onMutate: async (variables) => {
				// Optimistic update
				if (variables.solicitationFormId) {
					const newStatus = variables.isDraft ? "draft" : "submitted";
					setOptimisticFormStatus((prev) => ({
						...prev,
						[variables.solicitationFormId!]: newStatus,
					}));
				}
			},
			onError: (_error, variables) => {
				// Revert optimistic update on error
				if (variables.solicitationFormId) {
					setOptimisticFormStatus((prev) => {
						const newState = { ...prev };
						delete newState[variables.solicitationFormId!];
						return newState;
					});
				}
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ["vendorPortal", "formResponses"] });
			},
		}),
	);

	// Delete form response mutation
	const deleteFormResponseMutation = useMutation(
		trpc.vendorPortal.formResponses.delete.mutationOptions({
			onMutate: async (variables) => {
				// Optimistic update - mark as deleted
				if (variables.solicitationFormId) {
					setOptimisticFormStatus((prev) => ({
						...prev,
						[variables.solicitationFormId!]: "deleted",
					}));
				}
			},
			onError: (_error, variables) => {
				// Revert optimistic update on error
				if (variables.solicitationFormId) {
					setOptimisticFormStatus((prev) => {
						const newState = { ...prev };
						delete newState[variables.solicitationFormId!];
						return newState;
					});
				}
				toast.error("Failed to delete form response");
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ["vendorPortal", "formResponses"] });
				toast.success("Form response deleted");
			},
		}),
	);

	// Get download URL mutation
	const getDownloadUrlMutation = useMutation(
		trpc.vendorPortal.documentUploads.getDownloadUrl.mutationOptions({
			onError: (error) => {
				toast.error(`Failed to download: ${error.message}`);
			},
		}),
	);

	// Handle document download
	const handleDocumentDownload = async (doc: BidDocument) => {
		if (!doc.filePath) {
			toast.error("File path not found");
			return;
		}

		try {
			const result = await getDownloadUrlMutation.mutateAsync({
				filePath: doc.filePath,
			});

			// Open the signed URL in a new tab to trigger download
			window.open(result.signedUrl, "_blank");
		} catch {
			// Error already handled by mutation onError
		}
	};

	// Calculate progress
	const progress = useMemo(() => {
		const totalRequired =
			forms.filter((f) => f.required).length +
			documents.filter((d) => d.required).length +
			compliance.filter((c) => c.required).length;

		const completed =
			forms.filter((f) => f.required && f.completed).length +
			documents.filter((d) => d.required && d.uploaded).length +
			compliance.filter((c) => c.required && c.acknowledged).length;

		return totalRequired > 0
			? Math.round((completed / totalRequired) * 100)
			: 0;
	}, [forms, documents, compliance]);

	// Loading state
	const isLoading = bidLoading || solicitationLoading || formsLoading || documentsLoading || complianceLoading;

	const handleOpenFormModal = (form: BidForm) => {
		console.log("[EDIT_PAGE] Opening form modal for:", form.id, form.name);
		console.log("[EDIT_PAGE] Form existingResponses:", form.existingResponses);
		if (!form.formTemplateId) {
			toast.error("Form template not found");
			return;
		}
		setFormToFill(form);
	};

	const handleDeleteFormResponse = (formId: string) => {
		if (!vendorEmail) {
			toast.error("Please log in to delete form response");
			return;
		}
		deleteFormResponseMutation.mutate({
			solicitationFormId: formId,
			vendorEmail,
		});
	};

	const handleFormFillerClose = () => {
		setFormToFill(null);
	};

	const handleFormComplete = async (data: FormFillerData) => {
		console.log("[EDIT_PAGE] handleFormComplete called");
		console.log("[EDIT_PAGE] formToFill:", formToFill?.id, formToFill?.name);
		console.log("[EDIT_PAGE] data.isDraft:", data.isDraft);
		console.log("[EDIT_PAGE] data.fieldResponses:", data.fieldResponses);
		console.log("[EDIT_PAGE] data.signatureData:", data.signatureData ? "present" : "null");

		if (formToFill && vendorEmail) {
			// Persist form response to database immediately
			try {
				console.log("[EDIT_PAGE] Calling submitFormResponseMutation.mutateAsync...");
				const result = await submitFormResponseMutation.mutateAsync({
					solicitationFormId: formToFill.id,
					vendorEmail,
					fieldResponses: data.fieldResponses,
					isSigned: !!data.signatureData,
					signerName: data.signatureData?.signerName,
					signerEmail: data.signatureData?.signerEmail,
					signatureData: data.signatureData ?? null,
					isDraft: data.isDraft,
				});
				console.log("[EDIT_PAGE] submitFormResponseMutation result:", result);

				// Update local state based on whether it's a draft or final submission
				const newStatus = data.isDraft ? "draft" : "submitted";
				setForms(
					forms.map((f) =>
						f.id === formToFill.id
							? {
								...f,
								status: newStatus,
								completed: newStatus === "submitted",
								existingResponses: data.fieldResponses,
							}
							: f,
					),
				);
				// Store form responses locally for final submission
				setFormResponses((prev) => ({
					...prev,
					[formToFill.id]: {
						fieldResponses: data.fieldResponses,
						isSigned: !!data.signatureData,
					},
				}));
				toast.success(data.isDraft ? "Draft saved" : "Form submitted successfully");
			} catch (error) {
				console.error("[EDIT_PAGE] Failed to save form response:", error);
				toast.error(data.isDraft ? "Failed to save draft" : "Failed to save form response");
			}
		}
		setFormToFill(null);
	};

	const handleOpenUploadModal = (doc: BidDocument) => {
		setSelectedDocument(doc);
		setUploadModalOpen(true);
	};

	const handleDocumentUpload = (uploadInfo: { filePath: string; fileSize?: number; mimeType?: string; originalFilename?: string }) => {
		if (selectedDocument) {
			// Set optimistic state to persist through query refetches
			setOptimisticDocUploads((prev) => ({
				...prev,
				[selectedDocument.id]: {
					uploaded: true,
					filePath: uploadInfo.filePath,
					fileSize: uploadInfo.fileSize,
					mimeType: uploadInfo.mimeType,
					originalFilename: uploadInfo.originalFilename,
				},
			}));
			// Also update local state immediately for instant UI feedback
			setDocuments(
				documents.map((d) =>
					d.id === selectedDocument.id
						? {
								...d,
								uploaded: true,
								filePath: uploadInfo.filePath,
								fileSize: uploadInfo.fileSize,
								mimeType: uploadInfo.mimeType,
								originalFilename: uploadInfo.originalFilename,
							}
						: d,
				),
			);
		}
		setSelectedDocument(null);
	};

	const handleComplianceToggle = (compId: string) => {
		setCompliance(
			compliance.map((c) =>
				c.id === compId ? { ...c, acknowledged: !c.acknowledged } : c,
			),
		);
	};

	const handleSaveDraft = () => {
		if (!vendorEmail) {
			toast.error("Please log in to save draft");
			return;
		}

		// Build compliance acknowledgments payload
		const complianceAcknowledgments = compliance.map((c) => ({
			solicitationComplianceCheckId: c.solicitationComplianceCheckId,
			acknowledged: c.acknowledged,
		}));

		saveDraftMutation.mutate({
			bidId,
			vendorEmail,
			notes: undefined,
			complianceAcknowledgments,
		});
	};

	const handleSubmit = () => {
		if (!vendorEmail) {
			toast.error("Please log in to submit bid");
			return;
		}
		// Show confirmation dialog instead of submitting directly
		setSubmitConfirmOpen(true);
	};

	const confirmSubmit = async () => {
		setSubmitConfirmOpen(false);

		// Save compliance acknowledgments before submitting
		const complianceAcknowledgments = compliance.map((c) => ({
			solicitationComplianceCheckId: c.solicitationComplianceCheckId,
			acknowledged: c.acknowledged,
		}));

		try {
			// Save draft first to persist compliance acknowledgments
			await saveDraftMutation.mutateAsync({
				bidId,
				vendorEmail,
				notes: undefined,
				complianceAcknowledgments,
			});

			// Then submit the bid
			submitBidMutation.mutate({
				bidId,
				vendorEmail,
			});
		} catch (error) {
			toast.error("Failed to save compliance before submitting");
		}
	};

	const getStepStatus = (stepIndex: number) => {
		if (stepIndex < currentStep) return "completed";
		if (stepIndex === currentStep) return "current";
		return "upcoming";
	};

	const requiredFormsComplete = forms
		.filter((f) => f.required)
		.every((f) => f.completed);
	const requiredDocsUploaded = documents
		.filter((d) => d.required)
		.every((d) => d.uploaded);
	const requiredComplianceAcknowledged = compliance
		.filter((c) => c.required)
		.every((c) => c.acknowledged);

	// Check for specific issues with forms
	const requiredFormsMissing = forms.filter((f) => f.required && !f.status);
	const requiredFormsDraft = forms.filter((f) => f.required && f.status === "draft");
	const hasFormIssues = requiredFormsMissing.length > 0 || requiredFormsDraft.length > 0;

	const canSubmit =
		onboardingComplete &&
		requiredFormsComplete &&
		requiredDocsUploaded &&
		requiredComplianceAcknowledged;

	// Loading state
	if (isLoading) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</VendorLayout>
		);
	}

	// Bid not found
	if (!bidData) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Bid not found</h2>
					<p className="text-muted-foreground mt-2">
						The bid you're looking for doesn't exist.
					</p>
					<Button asChild className="mt-4">
						<Link to="/vendor/$buyerSlug/bids" params={{ buyerSlug }}>Back to My Bids</Link>
					</Button>
				</div>
			</VendorLayout>
		);
	}

	// Bid is not a draft
	if (bidData.status !== "draft") {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Cannot edit this bid</h2>
					<p className="text-muted-foreground mt-2">
						Only draft bids can be edited. This bid has status: {bidData.status}
					</p>
					<Button asChild className="mt-4">
						<Link to="/vendor/$buyerSlug/bids/$bidId" params={{ buyerSlug, bidId }}>
							View Bid
						</Link>
					</Button>
				</div>
			</VendorLayout>
		);
	}

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<Button variant="ghost" size="icon" asChild>
								<Link to="/vendor/$buyerSlug/bids" params={{ buyerSlug }}>
									<ArrowLeft className="h-4 w-4" />
								</Link>
							</Button>
							<Badge variant="outline">Draft</Badge>
							<Badge variant="secondary">{progress}% complete</Badge>
						</div>
						<h1 className="text-2xl font-bold tracking-tight">
							Edit Draft: {solicitation?.title || "Unknown Solicitation"}
						</h1>
						<div className="flex items-center gap-4 text-sm text-muted-foreground">
							<span className="flex items-center gap-1">
								<Building2 className="h-4 w-4" />
								{solicitation?.memberOrgName || solicitation?.buyerOrgName || "Unknown"}
							</span>
							<span className="flex items-center gap-1">
								<Calendar className="h-4 w-4" />
								{solicitation?.dueDate
									? `Due ${new Date(solicitation.dueDate).toLocaleDateString()}`
									: "No deadline set"}
							</span>
							<span className="flex items-center gap-1">
								<Clock className="h-4 w-4" />
								Last modified{" "}
								{bidData.updated_at
									? new Date(bidData.updated_at).toLocaleDateString()
									: "today"}
							</span>
						</div>
					</div>
					<Button variant="outline" onClick={handleSaveDraft}>
						<Save className="mr-2 h-4 w-4" />
						Save Draft
					</Button>
				</div>

				{/* Progress Bar */}
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center justify-between mb-2">
							<span className="text-sm font-medium">Overall Progress</span>
							<span className="text-sm text-muted-foreground">{progress}%</span>
						</div>
						<div className="h-2 bg-gray-100 rounded-full overflow-hidden">
							<div
								className="h-full bg-primary rounded-full transition-all duration-300"
								style={{ width: `${progress}%` }}
							/>
						</div>
					</CardContent>
				</Card>

				{/* Progress Steps */}
				<div className="flex items-center justify-between">
					{steps.map((step, index) => {
						const status = getStepStatus(index);
						return (
							<div key={step.id} className="flex items-center">
								<button
									type="button"
									className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
										status === "current"
											? "bg-primary/10 text-primary"
											: status === "completed"
												? "bg-primary/5 text-primary"
												: "text-muted-foreground"
									}`}
									onClick={() => setCurrentStep(index)}
								>
									{status === "completed" ? (
										<CheckCircle2 className="h-5 w-5" />
									) : status === "current" ? (
										<step.icon className="h-5 w-5" />
									) : (
										<Circle className="h-5 w-5" />
									)}
									<span className="hidden sm:inline font-medium">
										{step.name}
									</span>
								</button>
								{index < steps.length - 1 && (
									<div
										className={`w-8 h-0.5 mx-2 ${index < currentStep ? "bg-primary" : "bg-gray-200"}`}
									/>
								)}
							</div>
						);
					})}
				</div>

				{/* Step Content */}
				<Card>
					{/* Overview Step */}
					{steps[currentStep].id === "overview" && (
						<>
							<CardHeader>
								<CardTitle>Bid Overview</CardTitle>
								<CardDescription>
									Review the solicitation requirements before continuing your
									bid
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-6">
								<div className="p-4 bg-muted/50 rounded-lg">
									<h3 className="font-medium mb-2">Solicitation Summary</h3>
									<p className="text-sm text-muted-foreground">
										{solicitation?.description || "No description available"}
									</p>
								</div>

								<div className="p-4 border rounded-lg">
									<h4 className="font-medium mb-2">Submission Deadline</h4>
									<p className="text-lg">
										{solicitation?.dueDate
											? new Date(solicitation.dueDate).toLocaleDateString()
											: "Not set"}
									</p>
								</div>

								<Separator />

								<div>
									<h3 className="font-medium mb-4">Your Progress</h3>
									<div className="space-y-3">
										<div
											className={`flex items-center justify-between p-3 border rounded-lg ${requiredFormsComplete ? "bg-blue-50 border-blue-200" : ""}`}
										>
											<div className="flex items-center gap-2">
												<FileText
													className={`h-5 w-5 ${requiredFormsComplete ? "text-blue-600" : "text-muted-foreground"}`}
												/>
												<span>Forms to Complete</span>
											</div>
											<Badge
												variant={requiredFormsComplete ? "default" : "outline"}
											>
												{forms.filter((f) => f.required && f.completed).length}/
												{forms.filter((f) => f.required).length} complete
											</Badge>
										</div>
										<div
											className={`flex items-center justify-between p-3 border rounded-lg ${requiredDocsUploaded ? "bg-blue-50 border-blue-200" : ""}`}
										>
											<div className="flex items-center gap-2">
												<Upload
													className={`h-5 w-5 ${requiredDocsUploaded ? "text-blue-600" : "text-muted-foreground"}`}
												/>
												<span>Documents to Upload</span>
											</div>
											<Badge
												variant={requiredDocsUploaded ? "default" : "outline"}
											>
												{
													documents.filter((d) => d.required && d.uploaded)
														.length
												}
												/{documents.filter((d) => d.required).length} uploaded
											</Badge>
										</div>
										<div
											className={`flex items-center justify-between p-3 border rounded-lg ${requiredComplianceAcknowledged ? "bg-blue-50 border-blue-200" : ""}`}
										>
											<div className="flex items-center gap-2">
												<ClipboardCheck
													className={`h-5 w-5 ${requiredComplianceAcknowledged ? "text-blue-600" : "text-muted-foreground"}`}
												/>
												<span>Compliance Checks</span>
											</div>
											<Badge
												variant={
													requiredComplianceAcknowledged ? "default" : "outline"
												}
											>
												{
													compliance.filter((c) => c.required && c.acknowledged)
														.length
												}
												/{compliance.filter((c) => c.required).length}{" "}
												acknowledged
											</Badge>
										</div>
									</div>
								</div>
							</CardContent>
						</>
					)}

					{/* Forms Step */}
					{steps[currentStep].id === "forms" && (
						<>
							<CardHeader>
								<CardTitle>Required Forms</CardTitle>
								<CardDescription>
									Complete all required forms for this solicitation
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{forms.map((form) => (
									<div
										key={form.id}
										className={`flex items-start justify-between p-4 border rounded-lg ${
											form.status === "submitted"
												? "bg-blue-50 border-blue-200"
												: form.status === "draft"
												? "bg-amber-50 border-amber-200"
												: ""
										}`}
									>
										<div className="flex items-start gap-3">
											{form.status === "submitted" ? (
												<CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
											) : form.status === "draft" ? (
												<Clock className="h-5 w-5 text-amber-600 mt-0.5" />
											) : (
												<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
											)}
											<div>
												<div className="flex items-center gap-2">
													<span className="font-medium">{form.name}</span>
													<Badge
														variant={form.required ? "default" : "outline"}
													>
														{form.required ? "Required" : "Optional"}
													</Badge>
													{form.status === "submitted" && (
														<Badge variant="outline" className="text-blue-600 border-blue-300">
															Complete
														</Badge>
													)}
													{form.status === "draft" && (
														<Badge variant="outline" className="text-amber-600 border-amber-300">
															Draft
														</Badge>
													)}
												</div>
												<p className="text-sm text-muted-foreground mt-1">
													{form.description}
												</p>
											</div>
										</div>
										<div className="flex items-center gap-2">
											{form.status && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleDeleteFormResponse(form.id)}
													disabled={deleteFormResponseMutation.isPending}
													title="Delete response"
												>
													<Trash2 className="h-4 w-4 text-red-500" />
												</Button>
											)}
											<Button
												variant={form.status ? "outline" : "default"}
												size="sm"
												onClick={() => handleOpenFormModal(form)}
											>
												{form.status === "submitted"
													? "Edit"
													: form.status === "draft"
													? "Continue"
													: "Fill Out"}
											</Button>
										</div>
									</div>
								))}

								<div className="p-4 bg-muted/50 rounded-lg">
									<div className="flex items-center gap-2 text-sm">
										<Clock className="h-4 w-4" />
										<span>
											{forms.filter((f) => f.completed).length} of{" "}
											{forms.filter((f) => f.required).length} required forms
											completed
										</span>
									</div>
								</div>

								{/* Contract Period Pricing */}
								{solicitation?.contractPeriods &&
									solicitation.contractPeriods.length > 0 && (
										<>
											<Separator className="my-6" />
											<div className="space-y-4">
												<div className="flex items-center gap-2">
													<DollarSign className="h-5 w-5" />
													<h3 className="font-semibold">
														Contract Period Pricing
													</h3>
												</div>
												<p className="text-sm text-muted-foreground">
													Enter your proposed pricing for each contract period
												</p>

												<div className="space-y-4">
													{solicitation.contractPeriods.map((period) => (
														<div
															key={period.id}
															className="p-4 border rounded-lg"
														>
															<div className="flex items-center justify-between mb-2">
																<div>
																	<span className="font-medium">
																		{period.name}
																	</span>
																	<p className="text-sm text-muted-foreground">
																		{new Date(
																			period.startDate,
																		).toLocaleDateString()}{" "}
																		-{" "}
																		{new Date(
																			period.endDate,
																		).toLocaleDateString()}
																	</p>
																</div>
															</div>
															<div className="flex items-center gap-2">
																<span className="text-muted-foreground">$</span>
																<Input
																	type="text"
																	placeholder="0.00"
																	value={periodPricing[period.id] || ""}
																	onChange={(e) =>
																		handlePriceChange(period.id, e.target.value)
																	}
																	className="max-w-[200px]"
																/>
															</div>
														</div>
													))}
												</div>

												<div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
													<span className="font-semibold">
														Total Bid Amount
													</span>
													<span className="text-xl font-bold text-blue-600">
														{formatCurrency(getTotalPrice())}
													</span>
												</div>
											</div>
										</>
									)}
							</CardContent>
						</>
					)}

					{/* Documents Step */}
					{steps[currentStep].id === "documents" && (
						<>
							<CardHeader>
								<CardTitle>Required Documents</CardTitle>
								<CardDescription>
									Upload all required documents for your bid submission
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{documents.map((doc) => (
									<div
										key={doc.id}
										className={`flex items-start justify-between p-4 border rounded-lg ${
											doc.uploaded ? "bg-blue-50 border-blue-200" : ""
										}`}
									>
										<div className="flex items-start gap-3">
											{doc.uploaded ? (
												<CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
											) : (
												<Upload className="h-5 w-5 text-muted-foreground mt-0.5" />
											)}
											<div>
												<div className="flex items-center gap-2">
													<span className="font-medium">{doc.name}</span>
													<Badge variant={doc.required ? "default" : "outline"}>
														{doc.required ? "Required" : "Optional"}
													</Badge>
												</div>
												<p className="text-sm text-muted-foreground mt-1">
													{doc.description}
												</p>
												{doc.uploaded && (
													<p className="text-sm text-blue-600 mt-1">
														✓ Uploaded: {doc.originalFilename || `${doc.name.toLowerCase().replace(/ /g, "_")}.pdf`}
													</p>
												)}
											</div>
										</div>
										<div className="flex items-center gap-2">
											{doc.uploaded && doc.filePath && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleDocumentDownload(doc)}
													disabled={getDownloadUrlMutation.isPending}
													title="Download uploaded file"
												>
													{getDownloadUrlMutation.isPending ? (
														<Loader2 className="h-4 w-4 animate-spin" />
													) : (
														<Download className="h-4 w-4" />
													)}
												</Button>
											)}
											<Button
												variant={doc.uploaded ? "outline" : "default"}
												size="sm"
												onClick={() => handleOpenUploadModal(doc)}
											>
												{doc.uploaded ? "Replace" : "Upload"}
											</Button>
										</div>
									</div>
								))}

								<div className="p-4 bg-muted/50 rounded-lg">
									<div className="flex items-center gap-2 text-sm">
										<Clock className="h-4 w-4" />
										<span>
											{documents.filter((d) => d.uploaded).length} of{" "}
											{documents.filter((d) => d.required).length} required
											documents uploaded
										</span>
									</div>
								</div>
							</CardContent>
						</>
					)}

					{/* Compliance Step */}
					{steps[currentStep].id === "compliance" && (
						<>
							<CardHeader>
								<CardTitle>Compliance Acknowledgments</CardTitle>
								<CardDescription>
									Confirm that you meet all compliance requirements
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{compliance.map((comp) => (
									<div
										key={comp.id}
										className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
											comp.acknowledged
												? "bg-blue-50 border-blue-200"
												: "hover:bg-muted/50"
										}`}
										onClick={() => handleComplianceToggle(comp.id)}
									>
										<Checkbox
											checked={comp.acknowledged}
											onCheckedChange={() => handleComplianceToggle(comp.id)}
										/>
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<span className="font-medium">{comp.name}</span>
												<Badge variant={comp.required ? "default" : "outline"}>
													{comp.required ? "Required" : "Optional"}
												</Badge>
											</div>
											<p className="text-sm text-muted-foreground mt-1">
												{comp.description}
											</p>
										</div>
									</div>
								))}

								<div className="p-4 bg-muted/50 rounded-lg">
									<div className="flex items-center gap-2 text-sm">
										<Clock className="h-4 w-4" />
										<span>
											{compliance.filter((c) => c.acknowledged).length} of{" "}
											{compliance.filter((c) => c.required).length} required
											acknowledgments
										</span>
									</div>
								</div>
							</CardContent>
						</>
					)}

					{/* Review Step */}
					{steps[currentStep].id === "review" && (
						<>
							<CardHeader>
								<CardTitle>Review & Submit</CardTitle>
								<CardDescription>
									Review your bid before submitting
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-6">
								{/* Onboarding Warning */}
								{!onboardingComplete && (
									<div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
										<div className="flex items-start gap-3">
											<AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
											<div>
												<h3 className="font-medium text-amber-800">
													Complete Onboarding Required
												</h3>
												<p className="text-sm text-amber-700 mt-1">
													You must complete organization onboarding before submitting a bid.
													{onboardingProgress && onboardingProgress.overall.total > 0 && (
														<span className="block mt-1">
															Progress: {onboardingProgress.overall.completed}/{onboardingProgress.overall.total} items completed ({onboardingProgress.overall.percentage}%)
														</span>
													)}
												</p>
												<Button
													variant="outline"
													size="sm"
													className="mt-3"
													asChild
												>
													<Link to="/vendor/$buyerSlug/onboarding" params={{ buyerSlug }}>
														Go to Onboarding
													</Link>
												</Button>
											</div>
										</div>
									</div>
								)}

								{/* Completion Status */}
								<div className="space-y-3">
									<div
										className={`p-3 border rounded-lg ${requiredFormsComplete ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`}
									>
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												{requiredFormsComplete ? (
													<Check className="h-5 w-5 text-blue-600" />
												) : (
													<X className="h-5 w-5 text-red-600" />
												)}
												<span>Forms</span>
											</div>
											<span className="text-sm">
												{forms.filter((f) => f.completed).length}/
												{forms.filter((f) => f.required).length} required
												completed
											</span>
										</div>
										{hasFormIssues && (
											<div className="mt-2 text-sm text-red-700 space-y-1">
												{requiredFormsMissing.length > 0 && (
													<p>
														{requiredFormsMissing.length} required form{requiredFormsMissing.length > 1 ? "s" : ""} not started:{" "}
														{requiredFormsMissing.map((f) => f.name).join(", ")}
													</p>
												)}
												{requiredFormsDraft.length > 0 && (
													<p>
														{requiredFormsDraft.length} form{requiredFormsDraft.length > 1 ? "s" : ""} in draft status (must be finished):{" "}
														{requiredFormsDraft.map((f) => f.name).join(", ")}
													</p>
												)}
											</div>
										)}
									</div>

									<div
										className={`flex items-center justify-between p-3 border rounded-lg ${requiredDocsUploaded ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`}
									>
										<div className="flex items-center gap-2">
											{requiredDocsUploaded ? (
												<Check className="h-5 w-5 text-blue-600" />
											) : (
												<X className="h-5 w-5 text-red-600" />
											)}
											<span>Documents</span>
										</div>
										<span className="text-sm">
											{documents.filter((d) => d.uploaded).length}/
											{documents.filter((d) => d.required).length} required
											uploaded
										</span>
									</div>

									<div
										className={`flex items-center justify-between p-3 border rounded-lg ${requiredComplianceAcknowledged ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`}
									>
										<div className="flex items-center gap-2">
											{requiredComplianceAcknowledged ? (
												<Check className="h-5 w-5 text-blue-600" />
											) : (
												<X className="h-5 w-5 text-red-600" />
											)}
											<span>Compliance</span>
										</div>
										<span className="text-sm">
											{compliance.filter((c) => c.acknowledged).length}/
											{compliance.filter((c) => c.required).length} required
											acknowledged
										</span>
									</div>
								</div>

								<Separator />

								{/* Pricing Breakdown */}
								{solicitation?.contractPeriods &&
									solicitation.contractPeriods.length > 0 && (
										<div className="p-4 border rounded-lg">
											<h4 className="font-medium mb-3">Pricing Breakdown</h4>
											<div className="space-y-2">
												{solicitation.contractPeriods.map((period) => (
													<div
														key={period.id}
														className="flex justify-between text-sm"
													>
														<span className="text-muted-foreground">
															{period.name}:
														</span>
														<span>
															{formatCurrency(periodPricing[period.id] || 0)}
														</span>
													</div>
												))}
												<Separator className="my-2" />
												<div className="flex justify-between font-semibold">
													<span>Total Bid Amount:</span>
													<span className="text-blue-600">
														{formatCurrency(getTotalPrice())}
													</span>
												</div>
											</div>
										</div>
									)}

								{!canSubmit && (
									<div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
										<p className="text-sm text-yellow-800">
											Please complete all required items before submitting your
											bid.
										</p>
									</div>
								)}
							</CardContent>
						</>
					)}

					{/* Navigation */}
					<div className="flex items-center justify-between p-6 border-t">
						<Button
							variant="outline"
							onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
							disabled={currentStep === 0}
						>
							<ArrowLeft className="mr-2 h-4 w-4" />
							Previous
						</Button>

						{currentStep < steps.length - 1 ? (
							<Button onClick={() => setCurrentStep(currentStep + 1)}>
								Next
								<ArrowRight className="ml-2 h-4 w-4" />
							</Button>
						) : (
							<Button
								onClick={handleSubmit}
								disabled={!canSubmit || submitBidMutation.isPending}
								className="bg-primary hover:bg-primary/90"
							>
								{submitBidMutation.isPending ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<Send className="mr-2 h-4 w-4" />
								)}
								{submitBidMutation.isPending ? "Submitting..." : "Submit Bid"}
							</Button>
						)}
					</div>
				</Card>
			</div>

			{/* Document Upload Modal */}
			<DocumentUploadModal
				open={uploadModalOpen}
				onOpenChange={setUploadModalOpen}
				documentName={selectedDocument?.name || ""}
				documentDescription={selectedDocument?.description}
				requestedDocumentId={selectedDocument?.id || ""}
				vendorEmail={vendorEmail}
				onUploadComplete={handleDocumentUpload}
			/>

			{/* Form Fill Modal */}
			<PdfFormFillerV2
				open={!!formToFill}
				onClose={handleFormFillerClose}
				onSave={handleFormComplete}
				formName={formToFill?.name ?? ""}
				formDescription={formToFill?.description}
				formTemplateId={formToFill?.formTemplateId ?? ""}
				existingResponses={formToFill?.existingResponses ?? {}}
				requireSignature={true}
				signerName={`${vendorProfile.first_name} ${vendorProfile.last_name}`}
				signerEmail={signerEmail}
			/>

			{/* Submit Confirmation Modal */}
			<SubmitConfirmModal
				open={submitConfirmOpen}
				onOpenChange={setSubmitConfirmOpen}
				solicitationTitle={solicitation?.title || "this solicitation"}
				onConfirm={confirmSubmit}
				isPending={submitBidMutation.isPending}
			/>
		</VendorLayout>
	);
}
